// utils/exportUtils.js - Clean and secure export utilities for Vue.js application

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Array} columns - Column configuration (optional)
 * @returns {Object} Export result
 */
export const exportToCSV = (data, filename = 'export', columns = null) => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    // Use provided columns or extract from first object
    const headers = columns || Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
      // Header row
      headers.map(header => `"${header}"`).join(','),
      // Data rows
      ...data.map(row =>
        headers
          .map(header => {
            const value = getNestedValue(row, header);
            return `"${formatCellValue(value)}"`;
          })
          .join(',')
      ),
    ].join('\n');

    // Add BOM for UTF-8 encoding
    const blob = new Blob(['\ufeff' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    downloadFile(blob, `${filename}.csv`);

    return {
      success: true,
      filename: `${filename}.csv`,
      recordCount: data.length,
    };
  } catch (error) {
    console.error('CSV Export Error:', error);
    throw new Error(`Failed to export CSV: ${error.message}`);
  }
};

/**
 * Export data to Excel format using HTML table method
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {Array} columns - Column configuration (optional)
 * @returns {Object} Export result
 */
export const exportToExcel = (data, filename = 'export', columns = null) => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const headers = columns || Object.keys(data[0]);
    const timestamp = new Date().toISOString().split('T')[0];

    // Create HTML table with enhanced styling
    const htmlTable = `
      <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%;">
        <thead>
          <tr style="background-color: #f2f2f2; font-weight: bold;">
            ${headers
              .map(
                header =>
                  `<th style="border: 1px solid #ddd; padding: 8px; text-align: left;">${escapeHtml(
                    header
                  )}</th>`
              )
              .join('')}
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              row => `
            <tr>
              ${headers
                .map(header => {
                  const value = getNestedValue(row, header);
                  return `<td style="border: 1px solid #ddd; padding: 8px;">${escapeHtml(
                    formatCellValue(value)
                  )}</td>`;
                })
                .join('')}
            </tr>
          `
            )
            .join('')}
        </tbody>
      </table>
    `;

    // Create Excel file using HTML format
    const excelContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" 
            xmlns:x="urn:schemas-microsoft-com:office:excel" 
            xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>${escapeHtml(filename)}</x:Name>
                  <x:WorksheetOptions>
                    <x:DisplayGridlines/>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>${escapeHtml(filename)} - Generated on ${timestamp}</h2>
          ${htmlTable}
        </body>
      </html>
    `;

    const blob = new Blob([excelContent], {
      type: 'application/vnd.ms-excel',
    });

    downloadFile(blob, `${filename}.xls`);

    return {
      success: true,
      filename: `${filename}.xls`,
      recordCount: data.length,
    };
  } catch (error) {
    console.error('Excel Export Error:', error);
    throw new Error(`Failed to export Excel: ${error.message}`);
  }
};

/**
 * Export employees data with proper formatting
 * Compatible with Vue.js Employee Management component
 * @param {Array} employeesData - Employee records from Vue component
 * @param {string} filename - File name (optional)
 * @param {string} format - Export format ('csv' or 'excel')
 * @returns {Object} Export result
 */
export const exportEmployeesToExcel = (
  employeesData,
  filename = 'employees_export',
  format = 'excel'
) => {
  try {
    if (!employeesData || employeesData.length === 0) {
      throw new Error('No employee data to export');
    }

    // Format data for export with proper column names
    const formattedData = employeesData.map(employee => ({
      'Employee ID': employee.employee_id || employee.id || '',
      'Full Name': employee.name || '',
      'Email Address': employee.email || '',
      'Phone Number': employee.phone || '',
      Department: employee.department || '',
      Position: employee.position || '',
      'Work Schedule': employee.can_work_field
        ? 'Field Work Enabled'
        : 'Office Based',
      Status: employee.is_active ? 'Active' : 'Inactive',
      'Field Approval Required': employee.requires_field_approval
        ? 'Yes'
        : 'No',
      'Created Date': formatDate(employee.created_at),
      'Last Updated': formatDate(employee.updated_at),
      'Last Login': employee.last_login
        ? formatDate(employee.last_login)
        : 'Never',
    }));

    const timestamp = new Date().toISOString().split('T')[0];
    const finalFilename = `${filename}_${timestamp}`;

    if (format === 'csv') {
      return exportToCSV(formattedData, finalFilename);
    } else {
      return exportToExcel(formattedData, finalFilename);
    }
  } catch (error) {
    console.error('Employee Export Error:', error);
    throw error;
  }
};

/**
 * Export attendance data with proper formatting
 * @param {Array} attendanceData - Attendance records
 * @param {string} filename - File name (optional)
 * @param {string} format - Export format ('csv' or 'excel')
 * @returns {Object} Export result
 */
export const exportAttendanceToExcel = (
  attendanceData,
  filename = 'attendance_report',
  format = 'excel'
) => {
  try {
    if (!attendanceData || attendanceData.length === 0) {
      throw new Error('No attendance data to export');
    }

    const formattedData = attendanceData.map(record => ({
      'Employee ID': record.employee_id || record.employee?.employee_id || '',
      'Employee Name': record.employee_name || record.employee?.name || '',
      Department: record.department || record.employee?.department || '',
      Date: formatDate(record.date || record.clock_in),
      'Clock In': record.clock_in ? formatTime(record.clock_in) : 'N/A',
      'Clock Out': record.clock_out ? formatTime(record.clock_out) : 'N/A',
      'Hours Worked': calculateWorkingHours(record.clock_in, record.clock_out),
      'Location Method': record.location_method || 'Office',
      'Trust Score': record.trust_score || 100,
      Status: formatAttendanceStatus(record),
    }));

    const timestamp = new Date().toISOString().split('T')[0];
    const finalFilename = `${filename}_${timestamp}`;

    if (format === 'csv') {
      return exportToCSV(formattedData, finalFilename);
    } else {
      return exportToExcel(formattedData, finalFilename);
    }
  } catch (error) {
    console.error('Attendance Export Error:', error);
    throw error;
  }
};

/**
 * Export leave requests to Excel
 * @param {Array} leaveRequests - Leave request records
 * @param {string} filename - File name (optional)
 * @param {string} format - Export format ('csv' or 'excel')
 * @returns {Object} Export result
 */
export const exportLeaveRequestsToExcel = (
  leaveRequests,
  filename = 'leave_requests',
  format = 'excel'
) => {
  try {
    if (!leaveRequests || leaveRequests.length === 0) {
      throw new Error('No leave requests to export');
    }

    const formattedData = leaveRequests.map(request => ({
      'Request ID': request.id || '',
      'Employee ID': request.emp_id || request.employee_id || '',
      'Employee Name': request.employee_name || '',
      Department: request.department || '',
      'Leave Type': request.leave_type || '',
      'Start Date': formatDate(request.start_date),
      'End Date': formatDate(request.end_date),
      Days: calculateLeaveDays(request.start_date, request.end_date),
      Reason: request.reason || '',
      Status: capitalizeFirst(request.status || ''),
      Submitted: formatDate(request.created_at),
      Reviewed: request.reviewed_at
        ? formatDate(request.reviewed_at)
        : 'Pending',
      'Admin Remarks': request.admin_remarks || '',
    }));

    const timestamp = new Date().toISOString().split('T')[0];
    const finalFilename = `${filename}_${timestamp}`;

    if (format === 'csv') {
      return exportToCSV(formattedData, finalFilename);
    } else {
      return exportToExcel(formattedData, finalFilename);
    }
  } catch (error) {
    console.error('Leave Requests Export Error:', error);
    throw error;
  }
};

/**
 * Create and download employee import template
 * Compatible with Employee Management component
 * @returns {Object} Template creation result
 */
export const downloadEmployeeImportTemplate = () => {
  try {
    const templateData = [
      {
        'Full Name': 'John Smith',
        'Email Address': 'john.smith@company.com',
        'Phone Number': '(555) 123-4567',
        Department: 'Engineering',
        Position: 'Software Developer',
        Password: 'TempPass123!',
        'Field Work Enabled': 'No',
        'Requires Approval': 'Yes',
      },
      {
        'Full Name': 'Jane Doe',
        'Email Address': 'jane.doe@company.com',
        'Phone Number': '(555) 987-6543',
        Department: 'Marketing',
        Position: 'Marketing Manager',
        Password: 'SecurePass456!',
        'Field Work Enabled': 'Yes',
        'Requires Approval': 'Yes',
      },
    ];

    // Create instructions HTML
    const instructionsHtml = `
      <h3>Employee Import Template Instructions</h3>
      <table border="1" cellpadding="5" cellspacing="0" style="margin-bottom: 20px; border-collapse: collapse;">
        <tr style="background-color: #e7f3ff;">
          <th style="border: 1px solid #ddd; padding: 8px;">Field</th>
          <th style="border: 1px solid #ddd; padding: 8px;">Required</th>
          <th style="border: 1px solid #ddd; padding: 8px;">Format/Notes</th>
        </tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Full Name</td><td style="border: 1px solid #ddd; padding: 8px;">Yes</td><td style="border: 1px solid #ddd; padding: 8px;">Employee's complete name</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Email Address</td><td style="border: 1px solid #ddd; padding: 8px;">Yes</td><td style="border: 1px solid #ddd; padding: 8px;">Valid email address (must be unique)</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Phone Number</td><td style="border: 1px solid #ddd; padding: 8px;">No</td><td style="border: 1px solid #ddd; padding: 8px;">Contact phone number</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Department</td><td style="border: 1px solid #ddd; padding: 8px;">Yes</td><td style="border: 1px solid #ddd; padding: 8px;">Employee's department</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Position</td><td style="border: 1px solid #ddd; padding: 8px;">Yes</td><td style="border: 1px solid #ddd; padding: 8px;">Job title/position</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Password</td><td style="border: 1px solid #ddd; padding: 8px;">Yes</td><td style="border: 1px solid #ddd; padding: 8px;">Temporary password (6+ characters)</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Field Work Enabled</td><td style="border: 1px solid #ddd; padding: 8px;">No</td><td style="border: 1px solid #ddd; padding: 8px;">Yes/No - Can employee work in field</td></tr>
        <tr><td style="border: 1px solid #ddd; padding: 8px;">Requires Approval</td><td style="border: 1px solid #ddd; padding: 8px;">No</td><td style="border: 1px solid #ddd; padding: 8px;">Yes/No - Does field work require approval</td></tr>
      </table>
      <h4>Sample Data (Delete these rows and add your data):</h4>
    `;

    // Create full Excel content with instructions
    const excelContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" 
            xmlns:x="urn:schemas-microsoft-com:office:excel" 
            xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <style>
            table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            h3, h4 { color: #333; }
          </style>
        </head>
        <body>
          ${instructionsHtml}
          <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse;">
            <thead>
              <tr style="background-color: #f2f2f2; font-weight: bold;">
                ${Object.keys(templateData[0])
                  .map(
                    header =>
                      `<th style="border: 1px solid #ddd; padding: 8px;">${escapeHtml(
                        header
                      )}</th>`
                  )
                  .join('')}
              </tr>
            </thead>
            <tbody>
              ${templateData
                .map(
                  row => `
                <tr>
                  ${Object.values(row)
                    .map(
                      value =>
                        `<td style="border: 1px solid #ddd; padding: 8px;">${escapeHtml(
                          value
                        )}</td>`
                    )
                    .join('')}
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
          <p><strong>Note:</strong> Delete the sample rows and add your own employee data. Keep the header row intact.</p>
        </body>
      </html>
    `;

    const blob = new Blob([excelContent], {
      type: 'application/vnd.ms-excel',
    });

    downloadFile(blob, 'employee_import_template.xls');

    return {
      success: true,
      filename: 'employee_import_template.xls',
    };
  } catch (error) {
    console.error('Template Creation Error:', error);
    throw new Error(`Failed to create template: ${error.message}`);
  }
};

/**
 * Parse Excel/CSV file and return employee data
 * Compatible with Employee Management component
 * @param {File} file - Excel/CSV file to parse
 * @returns {Promise<Array>} Parsed employee data
 */
export const parseExcelFile = async file => {
  return new Promise((resolve, reject) => {
    // Validate file first
    const validation = validateExcelFile(file);
    if (!validation.isValid) {
      reject(new Error(validation.errors.join(', ')));
      return;
    }

    const reader = new FileReader();

    reader.onload = e => {
      try {
        const data = e.target.result;
        let parsedData;

        if (file.name.toLowerCase().endsWith('.csv')) {
          parsedData = parseCSVData(data);
        } else {
          // Handle Excel files (basic parsing for .xls files)
          parsedData = parseExcelData(data);
        }

        // Convert to employee format expected by Vue component
        const employees = convertToEmployeeFormat(parsedData);
        resolve(employees);
      } catch (error) {
        reject(new Error('Failed to parse file: ' + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  });
};

/**
 * Validate Excel/CSV file before processing
 * @param {File} file - File to validate
 * @returns {Object} Validation result
 */
export const validateExcelFile = file => {
  const errors = [];

  // Check file type
  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/csv',
  ];

  const fileExtension = file.name.toLowerCase().split('.').pop();
  const allowedExtensions = ['xlsx', 'xls', 'csv'];

  if (
    !allowedTypes.includes(file.type) &&
    !allowedExtensions.includes(fileExtension)
  ) {
    errors.push('File must be an Excel file (.xlsx, .xls) or CSV file (.csv)');
  }

  // Check file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    errors.push('File size must be less than 10MB');
  }

  // Check if file is empty
  if (file.size === 0) {
    errors.push('File is empty');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper functions

/**
 * Get nested object value using dot notation
 * @param {Object} obj - Source object
 * @param {string} path - Property path (e.g., 'employee.name')
 * @returns {any} The nested value
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, prop) => {
    return current && current[prop] !== undefined ? current[prop] : '';
  }, obj);
}

/**
 * Format cell value for CSV/Excel export
 * @param {any} value - Cell value
 * @returns {string} Formatted value
 */
function formatCellValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  // Convert to string and escape quotes
  return String(value).replace(/"/g, '""');
}

/**
 * Escape HTML characters for Excel export
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * Format date for display
 * @param {string} dateString - Date string
 * @returns {string} Formatted date
 */
function formatDate(dateString) {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Format time for display
 * @param {string} timeString - Time string or datetime string
 * @returns {string} Formatted time
 */
function formatTime(timeString) {
  if (!timeString) return '';

  try {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    return timeString;
  }
}

/**
 * Format attendance status
 * @param {Object} record - Attendance record
 * @returns {string} Formatted status
 */
function formatAttendanceStatus(record) {
  if (!record.clock_in) return 'Absent';
  if (!record.clock_out) return 'Incomplete';

  const workingHours = calculateWorkingHours(record.clock_in, record.clock_out);
  if (workingHours === 'N/A') return 'Present';

  const hours = parseFloat(workingHours.split(':')[0]);
  if (hours >= 8) return 'Full Day';
  if (hours >= 4) return 'Half Day';
  return 'Short Day';
}

/**
 * Calculate working hours between clock in and clock out
 * @param {string} clockIn - Clock in time
 * @param {string} clockOut - Clock out time
 * @returns {string} Working hours in HH:MM format
 */
function calculateWorkingHours(clockIn, clockOut) {
  if (!clockIn || !clockOut) return 'N/A';

  try {
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end - start;

    if (diffMs < 0) return 'Invalid';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    return 'N/A';
  }
}

/**
 * Calculate number of days between two dates
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {number} Number of days
 */
function calculateLeaveDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return diffDays;
  } catch (error) {
    return 0;
  }
}

/**
 * Capitalize first letter
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Parse CSV data
 * @param {string} csvData - CSV content
 * @returns {Array} Parsed rows
 */
function parseCSVData(csvData) {
  const lines = csvData.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header and one data row');
  }

  const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
  const rows = lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.replace(/"/g, '').trim());
    const obj = {};
    headers.forEach((header, index) => {
      obj[header] = values[index] || '';
    });
    return obj;
  });

  return rows;
}

/**
 * Simple Excel parser (basic implementation)
 * @param {string} data - Binary string data
 * @returns {Array} Parsed rows
 */
function parseExcelData(data) {
  const lines = data.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('File must have at least a header and one data row');
  }

  // Try to detect delimiter
  const delimiter = detectDelimiter(lines[0]);
  const headers = lines[0]
    .split(delimiter)
    .map(h => h.replace(/["\t]/g, '').trim());

  const rows = lines.slice(1).map(line => {
    const values = line
      .split(delimiter)
      .map(v => v.replace(/["\t]/g, '').trim());
    const obj = {};
    headers.forEach((header, index) => {
      obj[header] = values[index] || '';
    });
    return obj;
  });

  return rows;
}

/**
 * Detect delimiter in data
 * @param {string} line - First line to analyze
 * @returns {string} Best delimiter
 */
function detectDelimiter(line) {
  const delimiters = ['\t', ',', ';', '|'];
  let maxCount = 0;
  let bestDelimiter = ',';

  delimiters.forEach(delimiter => {
    const count = line.split(delimiter).length - 1;
    if (count > maxCount) {
      maxCount = count;
      bestDelimiter = delimiter;
    }
  });

  return bestDelimiter;
}

/**
 * Convert parsed data to employee format expected by Vue component
 * @param {Array} parsedData - Raw parsed data
 * @returns {Array} Formatted employee objects
 */
function convertToEmployeeFormat(parsedData) {
  return parsedData
    .map(row => {
      // Map various possible column names to our standard format
      const employee = {
        name: row['Full Name'] || row['Name'] || row['name'] || '',
        email: row['Email Address'] || row['Email'] || row['email'] || '',
        phone: row['Phone Number'] || row['Phone'] || row['phone'] || '',
        department: row['Department'] || row['department'] || '',
        position: row['Position'] || row['position'] || '',
        password:
          row['Password'] || row['password'] || generateDefaultPassword(),
        can_work_field: parseBooleanValue(
          row['Field Work Enabled'] || row['Can Work Field'] || false
        ),
        requires_field_approval: parseBooleanValue(
          row['Requires Approval'] || row['Requires Field Approval'] || true
        ),
      };

      // Only include employees with required fields
      if (!employee.name || !employee.email) {
        return null;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(employee.email)) {
        console.warn(
          `Invalid email format for ${employee.name}: ${employee.email}`
        );
        return null;
      }

      // Set default values for required fields
      if (!employee.department) {
        employee.department = 'General';
      }

      if (!employee.position) {
        employee.position = 'Employee';
      }

      return employee;
    })
    .filter(Boolean); // Remove null entries
}

/**
 * Parse boolean values from various formats
 * @param {any} value - Value to parse as boolean
 * @returns {boolean} Parsed boolean value
 */
function parseBooleanValue(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase().trim();
    return ['yes', 'true', '1', 'y', 'enabled', 'on'].includes(lowerValue);
  }
  return Boolean(value);
}

/**
 * Generate a default password
 * @returns {string} Generated password
 */
function generateDefaultPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

/**
 * Download file to user's computer
 * @param {Blob} blob - File blob
 * @param {string} filename - File name
 */
function downloadFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 100);
}

// Export utilities for different data types
export const exportUtils = {
  attendance: exportAttendanceToExcel,
  employees: exportEmployeesToExcel,
  leaveRequests: exportLeaveRequestsToExcel,
  csv: exportToCSV,
  excel: exportToExcel,
};

// Legacy exports for backward compatibility
export const exportAttendanceData = exportAttendanceToExcel;
export const exportEmployeeData = exportEmployeesToExcel;

// Default export
export default {
  exportToCSV,
  exportToExcel,
  exportEmployeesToExcel,
  exportAttendanceToExcel,
  exportLeaveRequestsToExcel,
  downloadEmployeeImportTemplate,
  parseExcelFile,
  validateExcelFile,
  exportUtils,
};
